import React, { useState, useEffect } from 'react';
import {
    Grid,
    Box,
    Typography,
    Button,
    makeStyles,
} from '@material-ui/core';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import OtpInput from 'react-otp-input';

const BLUE = '#1a73e8';
const GRAY = '#8c8c8c';
const OTP_LENGTH = 8;
const RESEND_SECONDS = 60;

const useStyles = makeStyles((theme) => ({
    containerOtp: {
        border: 'solid 1px #d9d9d9 ',
        borderRadius: 10,
        marginTop: theme.spacing(5),
        width: 'calc(100% - 40%)',
        marginLeft: 'auto',
        marginRight: 'auto',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
    },
    instructionBox: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(4),
    },
    phoneIcon: {
        fontSize: 32,
        marginRight: theme.spacing(2),
        paddingTop: theme.spacing(0.5),
    },
    textOtp: {
        fontSize: '1rem',
        color: theme.palette.text.primary,
        lineHeight: 1.5,
        textAlign: 'left',
    },
    pinFieldContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginLeft: 0,
            marginRight: 0,
        },
    },
    otpInput: {
        width: '36px !important',
        height: '40px !important',
        fontSize: '1.1rem',
        textAlign: 'center',
        border: `1px solid ${theme.palette.grey[400]}`,
        borderRadius: theme.shape.borderRadius,
        outline: 'none',
        transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        margin: '0 4px',
        '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
        },
    },
    resendRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(2.5, 2.5, 2),
    },
    resendLabel: {
        fontSize: '1rem',
        lineHeight: 1.5,
        textAlign: 'left',
    },
    resendLabelActive: {
        color: BLUE,
    },
    resendLabelIdle: {
        color: GRAY,
    },
    resendButton: {
        fontSize: '1rem',
        lineHeight: 1.5,
        fontWeight: 500,
        textTransform: 'none',
        color: BLUE,
        padding: 0,
        minWidth: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            textDecoration: 'underline',
        },
        '&.Mui-disabled': {
            color: GRAY,
            backgroundColor: 'transparent',
        },
    },
}));

const formatCountdown = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const SetOtpComponent = (props) => {
    const classes = useStyles();
    const {
        setOtp,
        otp,
        handleContinueClick,
        handleAuthorizationClick,
        loading,
        stop,
        setStopCountDown,
        isRejected
    } = props;
    const [seconds, setSeconds] = useState(RESEND_SECONDS);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (stop) {
            stopCountdown();
            if (setStopCountDown) setStopCountDown(false);
        }
        // eslint-disable-next-line
    }, [stop]);

    useEffect(() => {
        let interval = null;
        if (isActive && seconds > 0) {
            interval = setInterval(() => {
                setSeconds(prevSeconds => prevSeconds - 1);
            }, 1000);
        } else if (seconds === 0) {
            setIsActive(false);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isActive, seconds]);

    // Solo dígitos (la librería ya maneja borrado/retroceso y pegado)
    const handleChangeOtp = (value) => {
        if (value === '' || /^\d+$/.test(value)) {
            setOtp(value);
        }
    };

    const stopCountdown = () => {
        setIsActive(false);
        setSeconds(0);
    };

    const resetCountdown = () => {
        setIsActive(true);
        setSeconds(RESEND_SECONDS);
    };

    const canResend = !loading && (seconds === 0 || isRejected);

    const handleResendOtp = async () => {
        if (!canResend) return;
        if (setOtp) setOtp('');
        if (handleContinueClick) {
            const ok = await handleContinueClick();
            if (ok) {
                resetCountdown();
            } else {
                stopCountdown();
            }
        }
    };

    // WebOTP: autocompleta el código del SMS y autoriza con ese mismo código
    useEffect(() => {
        if ('OTPCredential' in window) {
            const abortController = new AbortController();
            navigator.credentials.get({
                otp: { transport: ['sms'] },
                signal: abortController.signal
            }).then(otpRes => {
                if (otpRes && otpRes.code) {
                    const numericCode = otpRes.code.replace(/\D/g, '').slice(0, OTP_LENGTH);
                    setOtp(numericCode);
                    if (numericCode.length === OTP_LENGTH && handleAuthorizationClick) {
                        setTimeout(() => {
                            handleAuthorizationClick(numericCode);
                        }, 100);
                    }
                }
            }).catch(() => {
                // AbortError o navegador sin soporte: se ignora
            });
            return () => abortController.abort();
        }
        // eslint-disable-next-line
    }, []);

    // Renderizado personalizado de cada input.
    // Se conservan los manejadores de la librería (onKeyDown/onInput/onChange/onPaste)
    // para que Backspace borre y retroceda al campo anterior automáticamente.
    const renderCustomInput = (inputProps, index) => {
        const isFocused = index === otp.length;

        return (
            <input
                {...inputProps}
                className={classes.otpInput}
                disabled={loading}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                onKeyDown={(e) => {
                    const allowedKeys = [
                        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'
                    ];
                    if (!allowedKeys.includes(e.key) && !(e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        return;
                    }
                    if (inputProps.onKeyDown) inputProps.onKeyDown(e);
                }}
                style={{
                    borderColor: isFocused ? BLUE : '#d9d9d9',
                    boxShadow: isFocused ? '0 0 0 2px rgba(26, 115, 232, 0.2)' : 'none'
                }}
            />
        );
    };

    const showCountdown = seconds > 0 && !isRejected;

    return (
        <Grid container className={classes.containerOtp}>
            <Grid item xs={12} sm={12} style={{ padding: 20 }}>
                <Box className={classes.instructionBox}>
                    <PhoneAndroidIcon className={classes.phoneIcon} />
                    <Typography variant="body1" className={classes.textOtp}>
                        Introduce el código enviado por tu Banco
                        a tu número telefónico para autorizar
                        de forma segura esta transacción.
                    </Typography>
                </Box>
            </Grid>
            <Grid container justify="center">
                <Grid item xs={12}>
                    <div className={classes.pinFieldContainer}>
                        <OtpInput
                            value={otp}
                            onChange={handleChangeOtp}
                            numInputs={OTP_LENGTH}
                            inputType="tel"
                            shouldAutoFocus
                            renderInput={renderCustomInput}
                            data-cy="otp-input"
                        />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.resendRow}>
                        {showCountdown ? (
                            <Typography
                                variant="body1"
                                className={`${classes.resendLabel} ${classes.resendLabelActive}`}
                            >
                                Puedes pedir otro código en: {formatCountdown(seconds)}
                            </Typography>
                        ) : (
                            <Typography
                                variant="body1"
                                className={`${classes.resendLabel} ${classes.resendLabelIdle}`}
                            >
                                ¿No te llegó el código?
                            </Typography>
                        )}
                        <Button
                            variant="text"
                            disableRipple
                            className={classes.resendButton}
                            onClick={handleResendOtp}
                            disabled={!canResend}
                        >
                            Reenviar código
                        </Button>
                    </div>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default SetOtpComponent;
